import fetcher from '@/services/fetcher'
import {
  type ContentUpdateEvent,
  RealTimeEvents,
} from '@/services/real-time-events'
import type {
  CmsApiResponse,
  Image,
  InstantWinCountersUpdateEvent,
  InstantWinDraw,
  InstantWinMainPrize,
  InstantWinPrize,
  InstantWinResult,
  InstantWinTicketCountersUpdateEvent,
  Nullable,
  Paginated,
} from '@/types'
import { isWithin48Hours, mapToDateTime } from '@/utils/date-time-utils'
import { EntryTypes, PrizeCategories } from '@/utils/helper-objects'
import { translateCurrency } from '@/utils/i18n-helpers'
import { toApiUrl, toCmsUrl } from '@/utils/url-generation-utils'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { useToast } from 'vue-toastification'

type InstantWinsStoreState = {
  allInstantWins: Map<string, InstantWinDraw>

  instantWins: Map<string, InstantWinDraw>
  fetchPromise: Promise<Array<InstantWinDraw>> | null
  ticketStatus: Nullable<
    Record<string, Array<InstantWinTicketCountersUpdateEvent>>
  >
  selectedEntryType: string
  winners: Map<string, Map<string, InstantWinResult & { image: Image }>>
  rawWinnersData: Array<InstantWinResult> // Store raw winners data for reprocessing
  hasLoadedAll: boolean // Track if we've fetched all instant wins (JSON + CMS)
  isLoading: boolean // Track if instant wins are currently being fetched
  hasError: boolean // Track if there was an error fetching instant wins
  lastFetchedAt: Nullable<DateTime> // Track when data was last fetched
}

export const useInstantWinsStore = defineStore('instant-wins', {
  state: (): InstantWinsStoreState => ({
    allInstantWins: new Map<string, InstantWinDraw>(),
    instantWins: new Map<string, InstantWinDraw>(),
    ticketStatus: null,
    fetchPromise: null,
    selectedEntryType: EntryTypes.Online,
    winners: new Map<
      string,
      Map<string, InstantWinResult & { image: Image }>
    >(),
    rawWinnersData: [],
    hasLoadedAll: false,
    isLoading: false,
    hasError: false,
    lastFetchedAt: null,
  }),

  actions: {
    async fetchResults(page: number = 1, threshold: number = 500) {
      const { error, data } = await fetcher.get<Paginated<InstantWinResult>>(
        toApiUrl(
          `instantwin/getWinners?current_page=${page}&currency_threshold=${threshold}`
        )
      )

      if (error.value || !data.value?.data) {
        throw error.value
      }

      const winnersData = data.value

      // Store raw winners data for potential reprocessing
      if (page === 1) {
        this.rawWinnersData = [...winnersData.data]
      } else {
        this.rawWinnersData.push(...winnersData.data)
      }

      this.processWinnersData(winnersData.data)

      return {
        winners: this.winners,
        currentPage: winnersData.current_page,
        hasMoreItems: winnersData.has_more_pages,
        rawData: winnersData,
      }
    },
    processWinnersData(winnersData: Array<InstantWinResult>) {
      for (const winner of winnersData) {
        const instantWin = this.instantWins.get(winner.instant_win_code)

        if (instantWin) {
          const updatedWinner = {
            ...winner,
            image: instantWin.main_prize.card_image,
          }

          const purchaseDateKey = updatedWinner.purchased_at.toISO() as string

          // Check if the winners already exists in the map for the purchase date
          const existingWinners = this.winners.get(purchaseDateKey) ?? new Map()

          // Check if the winner already exists in the existingWinners map for that purchase date
          if (!existingWinners.has(updatedWinner.ticket)) {
            existingWinners.set(updatedWinner.ticket, updatedWinner)
          }

          this.winners.set(purchaseDateKey, existingWinners)
        }
      }

      this.winners = new Map([...this.winners.entries()].sort().reverse())
    },
    reprocessWinners() {
      // Clear existing winners and reprocess all raw data with updated instant wins map
      this.winners.clear()
      this.processWinnersData(this.rawWinnersData)
    },
    async fetchInitialCounters(instantWin: InstantWinDraw) {
      try {
        const params = new URLSearchParams({ code: instantWin.code })

        const { data } = await fetcher.get<{
          success: boolean
          data: InstantWinCountersUpdateEvent
        }>(toApiUrl(`instantwin/get_counters?${params}`))

        if (data.value) {
          this.updateCounters(data.value.data)
        }
      } catch (error: any) {
        // TODO: Add Datadog custom log
      }
    },
    updateCounters(event: InstantWinCountersUpdateEvent) {
      const instantWin = this.instantWins.get(event.code)

      if (instantWin) {
        // Update the total purchased tickets
        instantWin.total_purchased_tickets = event.purchased_entries
        instantWin.total_remaining_prizes = event.total_remaining_prizes

        // Update prize counts and ticket statuses
        instantWin.prizes.forEach((prize) => {
          const updatedPrize = event.prizes.find(
            (p) => p.prize_code === prize.prizes_code
          )

          if (updatedPrize) {
            // Update prize won count
            prize.prizes_won_count = updatedPrize.won_count

            // Update ticket statuses if available
            if (updatedPrize.tickets) {
              prize.tickets = updatedPrize.tickets.map((ticket) => ({
                entry_code: ticket.entry_code,
                is_won: ticket.is_won,
              }))
            }
          }
        })

        // Store the updated ticket status for all prizes
        this.ticketStatus = {
          ...this.ticketStatus,
          [event.code]: event.prizes,
        }

        // Update the instantWin in the store
        this.instantWins.set(event.code, instantWin)
      }
    },
    /**
     * Load instant wins from local JSON files.
     * Returns an array of processed instant wins (does not mutate store state).
     */
    loadFromJson(): Array<InstantWinDraw> {
      const instantWinsFromJson = import.meta.glob(
        '../data/instant-wins/*.json',
        {
          import: 'default',
          eager: true,
        }
      )

      const results: Array<InstantWinDraw> = []

      for (const path in instantWinsFromJson) {
        const instantWin = instantWinsFromJson[path] as InstantWinDraw

        const translatedInstantWin: InstantWinDraw = translateCurrency(
          instantWin,
          this.$i18n.t('currencySymbol'),
          this.$i18n.t('currencyDecimalSymbol')
        )

        const typedInstantWin =
          mapToDateTime<InstantWinDraw>(translatedInstantWin)

        // If the instant win is within 48 hours of the close date, add the featured category
        if (
          isWithin48Hours(typedInstantWin.main_prize.close_at) &&
          !typedInstantWin.main_prize.categories.includes(
            PrizeCategories.Featured
          )
        ) {
          typedInstantWin.main_prize.categories.push(PrizeCategories.Featured)
        }

        results.push(typedInstantWin)
      }

      return results
    },
    /**
     * Load instant wins from the CMS API.
     * Returns an array of processed instant wins (does not mutate store state).
     */
    async loadFromCms(): Promise<Array<InstantWinDraw>> {
      const { data, error } = await fetcher.get<
        CmsApiResponse<Array<InstantWinDraw>>
      >(toCmsUrl('instant-wins'))

      if (error.value || !data.value?.data) {
        throw error.value || new Error('Failed to fetch instant wins from CMS')
      }

      const results: Array<InstantWinDraw> = []

      for (const instantWin of data.value.data) {
        // The fetcher already converts dates to DateTime objects and handles currency translation
        const typedInstantWin = instantWin

        // If the instant win is within 48 hours of the close date, add the featured category
        if (
          typedInstantWin.main_prize?.close_at &&
          isWithin48Hours(typedInstantWin.main_prize.close_at) &&
          !typedInstantWin.main_prize.categories.includes(
            PrizeCategories.Featured
          )
        ) {
          typedInstantWin.main_prize.categories.push(PrizeCategories.Featured)
        }

        results.push(typedInstantWin)
      }

      return results
    },
    /**
     * Fetch all instant wins from JSON and CMS in parallel.
     * JSON data takes priority over CMS. If CMS fails, JSON data is still displayed.
     */
    async fetchInstantWins(
      forceRefresh: boolean = false
    ): Promise<Array<InstantWinDraw>> {
      // If force refreshing, reset flags to reload everything
      if (forceRefresh) {
        this.hasLoadedAll = false
        this.instantWins = new Map()
      }

      // If we've loaded all instant wins and not forcing refresh, return cached data
      if (!forceRefresh && this.hasLoadedAll) {
        this.isLoading = false
        return this.instantWinDrawArray
      }

      // Return the existing fetch promise to prevent multiple simultaneous calls
      if (this.fetchPromise) {
        return this.fetchPromise
      }

      // Set loading state
      this.isLoading = true
      this.hasError = false

      this.fetchPromise = (async () => {
        try {
          // Load JSON and CMS in parallel
          const [jsonResult, cmsResult] = await Promise.allSettled([
            Promise.resolve(this.loadFromJson()),
            this.loadFromCms(),
          ])

          // Process JSON first — it takes priority over CMS
          if (jsonResult.status === 'fulfilled') {
            for (const iw of jsonResult.value) {
              this.instantWins.set(iw.code, iw)
              this.allInstantWins.set(iw.code, iw)
            }
          }

          // Merge CMS data — only add entries that don't already exist in JSON
          if (cmsResult.status === 'fulfilled') {
            for (const iw of cmsResult.value) {
              if (!this.instantWins.has(iw.code)) {
                this.instantWins.set(iw.code, iw)
              }
              if (!this.allInstantWins.has(iw.code)) {
                this.allInstantWins.set(iw.code, iw)
              }
            }
          } else {
            console.warn(
              'CMS fetch failed, using JSON fallback data',
              cmsResult.reason
            )
          }

          // If both sources failed, show error
          if (
            jsonResult.status === 'rejected' &&
            cmsResult.status === 'rejected'
          ) {
            this.hasError = true
            const toast = useToast()
            toast.error('Unable to load instant wins. Please try again later.')
          }

          this.hasLoadedAll = true
          this.lastFetchedAt = DateTime.now()

          return this.instantWinDrawArray
        } catch (error) {
          this.hasError = true
          console.error('Error fetching instant wins:', error)
          return this.instantWinDrawArray
        } finally {
          this.isLoading = false
          this.fetchPromise = null
        }
      })()

      return this.fetchPromise
    },
    getUpdatedTicketStatus(
      instantWinPrize: InstantWinPrize,
      instantWin: InstantWinDraw
    ): InstantWinTicketCountersUpdateEvent['tickets'] | undefined {
      return this.ticketStatus?.[instantWin.code]?.find(
        (prize: InstantWinTicketCountersUpdateEvent) =>
          prize.prize_code === instantWinPrize.prizes_code
      )?.tickets
    },
    /**
     * Subscribe to real-time content updates for instant wins.
     */
    subscribeToUpdates() {
      RealTimeEvents.subscribe(
        RealTimeEvents.Channels.ContentUpdates,
        RealTimeEvents.Events.InstantWinPublished,
        true,
        async (event: ContentUpdateEvent<InstantWinDraw>) => {
          console.log('Instant win content update received:', event)

          if (event.action === 'deleted') {
            this.instantWins.delete(event.code)
            this.allInstantWins.delete(event.code)
            return
          }

          // action === 'updated'
          if (event.data) {
            // Transform broadcast data the same way the fetcher does
            const transformed = mapToDateTime<InstantWinDraw>(
              translateCurrency(
                event.data,
                this.$i18n.t('currencySymbol'),
                this.$i18n.t('currencyDecimalSymbol')
              ) as InstantWinDraw
            )

            // Add featured category if within 48 hours of close
            if (
              transformed.main_prize?.close_at &&
              isWithin48Hours(transformed.main_prize.close_at) &&
              !transformed.main_prize.categories.includes(
                PrizeCategories.Featured
              )
            ) {
              transformed.main_prize.categories.push(PrizeCategories.Featured)
            }

            this.instantWins.set(event.code, transformed)
            this.allInstantWins.set(event.code, transformed)
          } else {
            // Fallback: fetch just this one instant win from CMS
            void this.getInstantWin(event.code, true)
          }
        }
      )

      RealTimeEvents.subscribe(
        RealTimeEvents.Channels.InstantWin,
        RealTimeEvents.Events.InstantWinCountersUpdate,
        true,
        (event: InstantWinCountersUpdateEvent) => {
          this.updateCounters(event)
        }
      )
    },
    /**
     * Unsubscribe from real-time content updates.
     */
    unsubscribeFromUpdates() {
      RealTimeEvents.unsubscribe(
        RealTimeEvents.Channels.ContentUpdates,
        RealTimeEvents.Events.InstantWinPublished
      )
      RealTimeEvents.unsubscribe(
        RealTimeEvents.Channels.InstantWin,
        RealTimeEvents.Events.InstantWinCountersUpdate
      )
    },
    /**
     * Force refresh all instant wins from the API.
     */
    async refreshInstantWins(): Promise<Array<InstantWinDraw>> {
      this.hasLoadedAll = false // Reset flag to force API call
      return this.fetchInstantWins(true)
    },
    async getInstantWin(
      instantWinCode: string,
      forceRefresh: boolean = false
    ): Promise<Nullable<InstantWinDraw>> {
      // Check if we already have this instant win cached
      if (!forceRefresh && this.instantWins.has(instantWinCode)) {
        return this.instantWins.get(instantWinCode) ?? null
      }

      try {
        let instantWin: InstantWinDraw | null = null

        // Try JSON first (priority source) using dynamic import
        try {
          const jsonModule = await import(
            `../data/instant-wins/${instantWinCode}.json`
          )

          if (jsonModule.default) {
            const translatedInstantWin: InstantWinDraw = translateCurrency(
              jsonModule.default,
              this.$i18n.t('currencySymbol'),
              this.$i18n.t('currencyDecimalSymbol')
            )

            instantWin = mapToDateTime<InstantWinDraw>(translatedInstantWin)
          }
        } catch (jsonError) {
          console.warn(
            `File not found for ${instantWinCode}, trying CMS:`,
            jsonError
          )
        }

        // If JSON didn't load, try CMS as fallback
        if (!instantWin) {
          const { data, error } = await fetcher.get<
            CmsApiResponse<InstantWinDraw>
          >(toCmsUrl(`instant-wins/${instantWinCode}`))

          if (error.value || !data.value?.data) {
            throw error.value || new Error('Instant win not found')
          }

          // The fetcher already handles date conversion and currency translation for CMS
          instantWin = data.value.data
        }

        if (!instantWin) {
          throw new Error(`Instant win ${instantWinCode} could not be loaded`)
        }

        // If the instant win is within 48 hours of the close date, add the featured category
        if (
          isWithin48Hours(instantWin.main_prize.close_at) &&
          !instantWin.main_prize.categories.includes(PrizeCategories.Featured)
        ) {
          instantWin.main_prize.categories.push(PrizeCategories.Featured)
        }

        // Cache the instant win
        this.instantWins.set(instantWin.code, instantWin)

        return instantWin
      } catch (error: any) {
        console.error('Failed to fetch instant win:', instantWinCode, error)

        // Only show error if we have no cached data
        // If it's in cache, user won't notice the fetch failure
        if (!this.instantWins.has(instantWinCode)) {
          const toast = useToast()
          toast.error('Unable to load this game. Please try another.')
        }

        return null
      }
    },
  },

  getters: {
    /**
     * Returns all Instant Win draws, with or without progress.
     */
    allInstantWinDrawArray({ allInstantWins }): Array<InstantWinDraw> {
      return Array.from(allInstantWins.values())
    },
    instantWinDrawArray({ instantWins }): Array<InstantWinDraw> {
      return Array.from(instantWins.values()).sort(
        (instantWin1: InstantWinDraw, instantWin2: InstantWinDraw) => {
          return (
            instantWin1.main_prize.close_at.toMillis() -
            instantWin2.main_prize.close_at.toMillis()
          )
        }
      )
    },
    futureInstantWinDraws(): Array<InstantWinDraw> {
      return this.instantWinDrawArray
        .filter(
          (instantWinDraw: InstantWinDraw) =>
            (instantWinDraw.is_visible ?? true) &&
            instantWinDraw.main_prize.close_at.diffNow().milliseconds >= 0 &&
            instantWinDraw.main_prize.open_at.diffNow().milliseconds < 0
        )
        .sort((a, b) => {
          const aTime =
            a.main_prize.close_at?.toMillis() ||
            a.main_prize.drawn_at.toMillis()
          const bTime =
            b.main_prize.close_at?.toMillis() ||
            b.main_prize.drawn_at.toMillis()
          return aTime - bTime
        })
    },
    pastInstantWinDraws(): Array<InstantWinDraw> {
      return this.instantWinDrawArray
        .filter(
          (instantWinDraw: InstantWinDraw) =>
            (instantWinDraw.is_visible ?? true) &&
            instantWinDraw.main_prize.close_at.diffNow().milliseconds < 0
        )
        .reverse()
    },
    futureInstantWinMainPrizes(): Array<InstantWinMainPrize> {
      return this.futureInstantWinDraws.map(
        (instantWinDraw: InstantWinDraw) => instantWinDraw.main_prize
      )
    },
    pastInstantWinMainPrizes(): Array<InstantWinMainPrize> {
      return this.pastInstantWinDraws.map(
        (instantWinDraw: InstantWinDraw) => instantWinDraw.main_prize
      )
    },
  },
})
